/**
 * VARIAS CUENTAS EN EL MISMO TELÉFONO.
 *
 * QUÉ PROBLEMA RESUELVE. En un club, un teléfono es de una familia: el papá
 * organiza, la hija juega la C y la mamá la B. Con una sola sesión, cambiarse
 * era cerrar sesión, acordarse del correo de la otra y escribir la
 * contraseña. Pasaba tres veces por sábado, y por eso terminaban jugando
 * todos desde la cuenta del papá — que es cómo un ranking deja de significar
 * algo.
 *
 * LIGAR PIDE CORREO Y CONTRASEÑA. No hay atajo: ligar una cuenta ES entrar a
 * esa cuenta, y lo único que se guarda después es el token que devuelve el
 * servidor. LA CONTRASEÑA NO SE GUARDA, ni cifrada ni codificada — se usa una
 * vez y se olvida.
 *
 * LIGAR NO ES CAMBIARSE, y son dos toques distintos a propósito: quien liga
 * la cuenta de su hija no quiere quedar adentro de la cuenta de su hija.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from './Alert';
import { Avatar } from './Avatar';
import { useAuth } from '../auth/AuthContext';
import { api, assetUrl } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';

export function AccountsSection(): JSX.Element {
  const { t } = useTranslation();
  const { session, accounts, linkAccount, switchTo, unlink } = useAuth();
  const [abierto, setAbierto] = useState(false);

  /**
   * LA PUESTA PRIMERO, el resto por nombre.
   *
   * El orden en que están guardadas es el orden en que se ligaron —y cambia
   * solo cuando una se actualiza—, así que la activa terminaba al final de la
   * lista: había que buscar con el ojo cuál era la propia en la pantalla que
   * existe justamente para saberlo.
   *
   * Por nombre y no por cuándo se ligó: la lista de cuentas de una familia no
   * cambia casi nunca, y alfabética es la única que se recuerda.
   */
  const ordenadas = useMemo(() => {
    const activa = session?.user.id;
    return [...accounts].sort((a, b) => {
      if (a.user.id === activa) return -1;
      if (b.user.id === activa) return 1;
      return a.user.displayName.localeCompare(b.user.displayName, 'es');
    });
  }, [accounts, session]);

  return (
    <section className="accounts">
      <div className="accounts-head">
        <h2>{t('accounts.title')}</h2>
        <button
          type="button"
          className="link accounts-add"
          aria-label={t('accounts.add')}
          aria-expanded={abierto}
          onClick={() => setAbierto((x) => !x)}
        >
          <span aria-hidden="true">{abierto ? '×' : '+'}</span>
        </button>
      </div>

      <p className="help">{t('accounts.help')}</p>

      {abierto && <LigarForm onLinked={linkAccount} onDone={() => setAbierto(false)} />}

      <ul className="accounts-list">
        {ordenadas.map((cuenta) => {
          const puesta = cuenta.user.id === session?.user.id;
          return (
            <li key={cuenta.user.id} className={puesta ? 'account account-on' : 'account'}>
              {/*
                TODA LA FILA ES EL BOTÓN DE CAMBIARSE, menos la cruz de quitar.
                Es lo que pidió Javier —«tocando el perfil»— y además es el
                blanco más grande posible en un teléfono. La que ya está
                puesta no es un botón: tocarla no haría nada, y un botón que
                no hace nada se toca dos veces y se cree que la app se colgó.
              */}
              {puesta ? (
                <span className="account-who">
                  <Avatar
                    url={assetUrl(cuenta.user.avatarPath)}
                    name={cuenta.user.displayName}
                    size={36}
                  />
                  <span className="account-name">{cuenta.user.displayName}</span>
                  <span className="tag account-tag">{t('accounts.active')}</span>
                </span>
              ) : (
                <button
                  type="button"
                  className="account-who account-switch"
                  aria-label={t('accounts.use', { name: cuenta.user.displayName })}
                  onClick={() => switchTo(cuenta.user.id)}
                >
                  <Avatar
                    url={assetUrl(cuenta.user.avatarPath)}
                    name={cuenta.user.displayName}
                    size={36}
                  />
                  <span className="account-name">{cuenta.user.displayName}</span>
                </button>
              )}

              <button
                type="button"
                className="link danger account-x"
                aria-label={t('accounts.remove', { name: cuenta.user.displayName })}
                onClick={() => unlink(cuenta.user.id)}
              >
                <span aria-hidden="true">−</span>
              </button>
            </li>
          );
        })}
      </ul>

      {/* Se dice ANTES de que pase: cerrar sesión se lleva todas, y quien no
          lo sabe vuelve a escribir cuatro contraseñas para enterarse. */}
      {accounts.length > 1 && <p className="help">{t('accounts.warning')}</p>}
    </section>
  );
}

function LigarForm({
  onLinked,
  onDone,
}: {
  onLinked: (respuesta: Awaited<ReturnType<typeof api.login>>) => void;
  onDone: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const { accounts } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function ligar(): Promise<void> {
    if (enviando) return;
    setEnviando(true);
    setError(null);
    try {
      const respuesta = await api.login({ email: email.trim(), password });
      // YA LIGADA: se dice, en vez de dejar que «funcione» sin cambiar nada.
      // Un formulario que se cierra sin efecto visible se lee como un error.
      if (accounts.some((a) => a.user.id === respuesta.user.id)) {
        setError(t('accounts.already'));
        return;
      }
      onLinked(respuesta);
      // La contraseña se borra del estado apenas se usa: no hace falta más, y
      // lo que no está en memoria no se filtra a ningún lado.
      setEmail('');
      setPassword('');
      onDone();
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="accounts-form">
      {error !== null && <Alert kind="error">{error}</Alert>}

      <div className="field">
        <label htmlFor="link-email">{t('accounts.email')}</label>
        <input
          id="link-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="link-password">{t('accounts.password')}</label>
        <input
          id="link-password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </div>

      <p className="accounts-actions">
        <button
          type="button"
          className="button-like"
          disabled={email.trim() === '' || password === '' || enviando}
          onClick={() => void ligar()}
        >
          {t('accounts.link')}
        </button>
        <button type="button" className="link" onClick={onDone}>
          {t('accounts.cancel')}
        </button>
      </p>
    </div>
  );
}
